/**
 * NoPaymentMethodError — typed error thrown by `OneTimeCredits.service`
 * when the BFF returns 409 (no payment method on file).
 *
 * Aligns with the repo's classified-error pattern (see `PurchaseCreditsModal.component`):
 * an `Error` with a `type` discriminator that consumers detect via
 * `instanceof NoPaymentMethodError` (preferred) or duck-type on `error.type`.
 *
 * Caught in `ConfirmPurchaseModal` to close the purchase modal and open the
 * AddPaymentMethod flow under SubscriptionsManager.
 */
export class NoPaymentMethodError extends Error {
  /** Discriminator matching the repo's `tClassifiedError` pattern. */
  readonly type = 'no_payment_method' as const;

  constructor(message = 'No payment method on file for this workspace.') {
    super(message);
    this.name = 'NoPaymentMethodError';
    // Restore prototype chain after extending built-in Error (TS target ES5+ safety).
    Object.setPrototypeOf(this, NoPaymentMethodError.prototype);
  }
}

/** Type-guard for non-instanceof contexts (e.g. structured-clone boundaries). */
export const isNoPaymentMethodError = (
  err: unknown,
): err is NoPaymentMethodError =>
  err instanceof NoPaymentMethodError ||
  (err instanceof Error &&
    'type' in err &&
    (err as { type?: unknown }).type === 'no_payment_method');
